import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  SelectDropdownComponent,
  ValidationRule,
} from "../../services/types/ComponentTypes";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import { fetchLookup } from "../../state/slices/lookupsSlice";
import { QuickAddModal } from "./QuickAddModal";

interface SelectDropdownProps {
  config: SelectDropdownComponent;
  value?: string | number | boolean | (string | number | boolean)[];
  onChange?: (
    name: string,
    value: string | number | boolean | (string | number | boolean)[]
  ) => void;
  onFocus?: () => void;
  externalError?: string;
}

export const SelectDropdown: React.FC<SelectDropdownProps> = ({
  config: rawConfig,
  value,
  onChange,
  onFocus,
  externalError,
}) => {
  // Default options to empty array since options may be loaded dynamically via lookupName
  const config = { ...rawConfig, options: rawConfig.options ?? [] };

  const dispatch = useAppDispatch();
  const lookupsState = useAppSelector((state) => state.lookups);
  const [isOpen, setIsOpen] = useState(false);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [error, setError] = useState<string>("");
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Log for get_all_ledgers dropdown
  useEffect(() => {
    if (config.lookupName === "get_all_ledgers") {
      console.log(
        `[get_all_ledgers] SelectDropdown Component Mounted/Updated:`,
        {
          name: config.name,
          optionsCount: config.options.length,
          options: config.options, // First 10 options
          value,
          valueType: typeof value,
          isOpen,
          timestamp: new Date().toISOString(),
        }
      );
    }
  }, [config.options, value, isOpen, config.lookupName, config.name]);

  const validateField = (
    inputValue:
      | string
      | number
      | boolean
      | (string | number | boolean)[]
      | undefined
  ): string => {
    if (!config.validation) return "";

    for (const rule of config.validation) {
      switch (rule.rule) {
        case ValidationRule.REQUIRED:
          if (
            inputValue === undefined ||
            inputValue === "" ||
            (Array.isArray(inputValue) && inputValue.length === 0)
          )
            return rule.message || "Please select an option";
          break;
      }
    }
    return "";
  };

  // Helper function to match value with option value (with robust type coercion)
  const matchValue = (
    optionValue: string | number | boolean,
    searchValue: string | number | boolean
  ): boolean => {
    // Exact match
    if (optionValue === searchValue) return true;

    // String comparison
    if (String(optionValue) === String(searchValue)) return true;

    // Number comparison (handle string numbers)
    const optNum =
      typeof optionValue === "number" ? optionValue : Number(optionValue);
    const searchNum =
      typeof searchValue === "number" ? searchValue : Number(searchValue);
    if (!isNaN(optNum) && !isNaN(searchNum) && optNum === searchNum)
      return true;

    // Boolean comparison
    if (typeof optionValue === "boolean" && typeof searchValue === "boolean") {
      return optionValue === searchValue;
    }

    // Handle string "true"/"false" vs boolean
    if (typeof optionValue === "boolean") {
      if (searchValue === "true" && optionValue === true) return true;
      if (searchValue === "false" && optionValue === false) return true;
    }
    if (typeof searchValue === "boolean") {
      if (optionValue === "true" && searchValue === true) return true;
      if (optionValue === "false" && searchValue === false) return true;
    }

    return false;
  };

  const handleOptionClick = (optionValue: string | number | boolean) => {
    if (config.multiple) {
      const currentValue = Array.isArray(value) ? value : [];
      let newValue: (string | number | boolean)[];

      if (currentValue.includes(optionValue)) {
        newValue = currentValue.filter((v) => v !== optionValue);
      } else {
        if (
          config.maxSelections &&
          currentValue.length >= config.maxSelections
        ) {
          return; // Don't add if max selections reached
        }
        newValue = [...currentValue, optionValue];
      }

      const validationError = validateField(newValue);
      setError(validationError);
      onChange?.(config.name, newValue);
    } else {
      const validationError = validateField(optionValue);
      setError(validationError);
      onChange?.(config.name, optionValue);
      // If this dropdown should also set the next_state workflow field
      if (config.enableNextState) {
        onChange?.('next_state', optionValue);
      }
      setIsOpen(false);
    }
  };

  const getDisplayValue = () => {
    if (config.multiple && Array.isArray(value)) {
      if (value.length === 0) return config.placeholder || "Select";
      const selectedLabels = value.map((v) => {
        // Use improved matching function
        const option = config.options.find((opt) => matchValue(opt.value, v));
        return option?.label || (import.meta.env.DEV ? `[${v}]` : "");
      });
      return selectedLabels.join(", ");
    } else {
      // CRITICAL: Try multiple matching strategies for edit mode
      // First try exact match with improved matching function
      let selectedOption = config.options.find((opt) =>
        matchValue(opt.value, value as string | number | boolean)
      );

      // If not found, try matching by id property (some lookups use id instead of value)
      if (!selectedOption && value !== undefined && value !== null && value !== "") {
        selectedOption = config.options.find((opt: any) => {
          const optId = opt.id !== undefined ? opt.id : (opt as any).codeid;
          return matchValue(optId, value as string | number | boolean);
        });
      }

      // If still not found, try matching value as string against option value as string
      if (!selectedOption && value !== undefined && value !== null && value !== "") {
        const valueStr = String(value);
        selectedOption = config.options.find((opt) => {
          const optValueStr = String(opt.value);
          const optIdStr = (opt as any).id !== undefined ? String((opt as any).id) : "";
          return optValueStr === valueStr || optIdStr === valueStr;
        });
      }

      // Enhanced debug logging for all dropdowns in edit mode
      if (import.meta.env.DEV && value !== undefined && value !== null && value !== "") {
        const isWorkflowField = config.lookupName === "Approve" || config.name === "approve_status";
        const logData = {
          fieldName: config.name,
          lookupName: config.lookupName,
          value,
          valueType: typeof value,
          valueString: String(value),
          optionsCount: config.options.length,
          foundOption: !!selectedOption,
          optionLabel: selectedOption?.label,
          optionValue: selectedOption?.value,
          optionId: (selectedOption as any)?.id,
          optionValueType: typeof selectedOption?.value,
          sampleOptions: config.options.slice(0, 3).map((opt) => ({
            label: opt.label,
            value: opt.value,
            id: (opt as any).id,
            valueType: typeof opt.value,
            valueString: String(opt.value),
            matches: matchValue(opt.value, value as string | number | boolean),
          })),
          valueExistsButNoMatch:
            config.options.length > 0 &&
            !selectedOption,
        };

        if (isWorkflowField) {
          console.log(`[SelectDropdown] WORKFLOW FIELD ${config.name} - getDisplayValue:`, logData);
        } else if (!selectedOption && config.options.length > 0) {
          // Only log warnings for non-workflow fields if value not found
          console.warn(`[SelectDropdown] ${config.name} - Value not found in options:`, logData);
        } else if (selectedOption) {
          console.log(`[SelectDropdown] ${config.name} - Value found in options:`, {
            fieldName: config.name,
            value,
            optionLabel: selectedOption.label,
            optionValue: selectedOption.value,
          });
        }
      }

      // Check if this is a "Rejected" status for workflow fields
      const isWorkflowField = config.lookupName === "Approve" || config.name === "approve_status";
      const isRejected = value && String(value).toLowerCase() === "rejected";

      // If found but label looks like an ID, show placeholder instead
      if (selectedOption) {
        const label = selectedOption.label;
        // Check if label looks like an ID (UUID or long alphanumeric)
        if (
          label &&
          (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(
            label
          ) ||
            (/^[0-9a-f-]+_[0-9_]+$/i.test(label) && label.length > 30) ||
            (/^[0-9]+$/.test(label) && label.length > 10))
        ) {
          return config.placeholder || "Select";
        }

        // Return label (styling will be applied by parent span)
        return label;
      }

      // In dev mode, show raw value if no match found (helps debug edit mode issues)
      if (import.meta.env.DEV && value && config.options.length > 0) {
        return `${value}`;
      }

      return config.placeholder || "Select";
    }
  };

  const isSelected = (optionValue: string | number | boolean) => {
    if (config.multiple && Array.isArray(value)) {
      return value.some((v) => matchValue(optionValue, v));
    }
    // Use improved matching function for consistency
    // Also try matching by id property for edit mode compatibility
    if (matchValue(optionValue, value as string | number | boolean)) {
      return true;
    }

    // Try matching value against option's id property
    if (value !== undefined && value !== null && value !== "") {
      const option = config.options.find((opt) => opt.value === optionValue);
      if (option) {
        const optId = (option as any).id !== undefined ? (option as any).id : (option as any).codeid;
        if (optId !== undefined && matchValue(optId, value as string | number | boolean)) {
          return true;
        }
      }
    }

    return false;
  };

  const updateDropdownPosition = () => {
    if (selectRef.current && isOpen) {
      const rect = selectRef.current.getBoundingClientRect();
      // Calculate position relative to body for absolute positioning
      // This accounts for scroll position so dropdown scrolls with page
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  // Reset option refs array when options change
  useEffect(() => {
    optionRefs.current = new Array(config.options.length).fill(null);
  }, [config.options.length]);

  // Initialize highlighted index when dropdown opens
  useEffect(() => {
    if (isOpen && config.options.length > 0) {
      // If there's a current value, highlight it; otherwise highlight first option
      if (!config.multiple && value !== undefined && value !== "" && value !== null) {
        const currentIndex = config.options.findIndex((opt) =>
          matchValue(opt.value, value as string | number | boolean)
        );
        if (currentIndex >= 0) {
          setHighlightedIndex(currentIndex);
          setTimeout(() => {
            const optionElement = optionRefs.current[currentIndex];
            if (optionElement) {
              optionElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
            }
          }, 50);
        } else {
          setHighlightedIndex(0);
        }
      } else {
        setHighlightedIndex(0);
      }
    } else if (!isOpen) {
      setHighlightedIndex(-1);
    }
  }, [isOpen, config.options.length, value, config.multiple]);

  useEffect(() => {
    if (isOpen) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        updateDropdownPosition();
      });
    } else {
      setDropdownPosition(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const handleScroll = () => updateDropdownPosition();
      const handleResize = () => updateDropdownPosition();

      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleResize);

      return () => {
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!target) return;

      // Check if click is outside all dropdown elements
      const isOutsideDropdown = !dropdownRef.current || !dropdownRef.current.contains(target);
      const isOutsideMenu = !dropdownMenuRef.current || !dropdownMenuRef.current.contains(target);
      const isOutsideSelect = !selectRef.current || !selectRef.current.contains(target);

      // Only close if click is outside all elements
      if (isOutsideDropdown && isOutsideMenu && isOutsideSelect) {
        setIsOpen(false);
      }
    };

    // Use capture phase to catch clicks before they bubble
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, [isOpen]);

  // CRITICAL: Force re-render when options load (for edit mode)
  // This ensures the dropdown updates when options become available
  const [optionsLoaded, setOptionsLoaded] = useState(false);

  // Watch for when options load and value is set (for edit mode auto-population)
  useEffect(() => {
    const hasValue = config.multiple
      ? Array.isArray(value) && value.length > 0
      : value !== undefined && value !== null && value !== "";
    const hasOptions = config.options.length > 0;

    // If options just loaded, trigger a re-render
    if (hasOptions && !optionsLoaded) {
      setOptionsLoaded(true);
    }

    if (hasValue && hasOptions) {
      // Options are now available, check if we can find a match using the improved matching function
      const findMatch = () => {
        if (config.multiple && Array.isArray(value)) {
          const unmatched = value.filter((v) => {
            return !config.options.find((opt) => matchValue(opt.value, v));
          });
          return unmatched.length === 0;
        } else {
          // Try multiple matching strategies
          let found = !!config.options.find((opt) =>
            matchValue(opt.value, value as string | number | boolean)
          );

          // If not found, try matching by id
          if (!found && value !== undefined && value !== null && value !== "") {
            found = !!config.options.find((opt: any) => {
              const optId = opt.id !== undefined ? opt.id : (opt as any).codeid;
              return matchValue(optId, value as string | number | boolean);
            });
          }

          // If still not found, try string comparison
          if (!found && value !== undefined && value !== null && value !== "") {
            const valueStr = String(value);
            found = !!config.options.find((opt) => {
              const optValueStr = String(opt.value);
              const optIdStr = (opt as any).id !== undefined ? String((opt as any).id) : "";
              return optValueStr === valueStr || optIdStr === valueStr;
            });
          }

          return found;
        }
      };

      const matchFound = findMatch();

      // Enhanced debug logging for workflow state fields (approve_status)
      const isWorkflowField = config.lookupName === "Approve" || config.name === "approve_status";

      if (import.meta.env.DEV) {
        if (matchFound) {
          if (isWorkflowField) {
            console.log(
              `[SelectDropdown] WORKFLOW FIELD ${config.name}: Options loaded, value matched successfully`,
              {
                fieldName: config.name,
                value,
                valueType: typeof value,
                valueString: String(value),
                optionsCount: config.options.length,
                lookupName: config.lookupName,
                matchingOption: config.options.find((opt) =>
                  matchValue(opt.value, value as string | number | boolean)
                ),
              }
            );
          } else {
            console.log(
              `[SelectDropdown] ${config.name}: Options loaded, value matched successfully`,
              {
                fieldName: config.name,
                value,
                valueType: typeof value,
                optionsCount: config.options.length,
                lookupName: config.lookupName,
              }
            );
          }
        } else {
          const logData = {
            fieldName: config.name,
            value,
            valueType: typeof value,
            valueString: String(value),
            optionsCount: config.options.length,
            lookupName: config.lookupName,
            allOptions: config.options.map((opt) => ({
              label: opt.label,
              value: opt.value,
              valueType: typeof opt.value,
              valueString: String(opt.value),
              matches: matchValue(opt.value, value as string | number | boolean),
            })),
          };

          if (isWorkflowField) {
            console.error(
              `[SelectDropdown] WORKFLOW FIELD ${config.name}: Options loaded but value NOT FOUND in options`,
              logData
            );
          } else {
            console.warn(
              `[SelectDropdown] ${config.name}: Options loaded but value not found in options`,
              logData
            );
          }
        }
      }
    } else if (hasValue && !hasOptions) {
      // Value is set but options haven't loaded yet (edit mode scenario)
      if (import.meta.env.DEV) {
        // Only log if we are NOT currently loading options, to avoid initialization noise
        const isLoadingLookup = lookupsState.loading[config.lookupName || ""] || false;
        if (!isLoadingLookup) {
          const isWorkflowField = config.lookupName === "Approve" || config.name === "approve_status";
          if (isWorkflowField) {
            console.log(
              `[SelectDropdown] WORKFLOW FIELD ${config.name}: Value set but options not yet loaded (waiting for workflow lookup)`,
              {
                fieldName: config.name,
                value,
                valueType: typeof value,
                valueString: String(value),
                lookupName: config.lookupName,
                expectedLookupKey: `workflow_service_Approve`,
              }
            );
          } else {
            console.log(
              `[SelectDropdown] ${config.name}: Value set but options not yet loaded (waiting for lookup)`,
              {
                fieldName: config.name,
                value,
                valueType: typeof value,
                lookupName: config.lookupName,
              }
            );
          }
        }
      }
    }
  }, [config.options, value, config.multiple, config.name, config.lookupName]);

  const getWidthClass = () => {
    if (config.style?.widthMode === "wrap") return "w-auto";
    return "w-full";
  };

  const handleQuickAddSuccess = (response: any) => {
    if (config.lookupName) {
      dispatch(fetchLookup({ lookupName: config.lookupName }));
    }

    // Auto-select the new item
    const newId = response?.data?.id || response?.id || response?.data?.uuid || response?.uuid;
    if (newId) {
      onChange?.(config.name, newId);
    }
  };

  const isDarkMode = document.documentElement.classList.contains('dark');
  const selectStyles: React.CSSProperties = {
    borderRadius: config.style?.borderRadius || 8,
    fontSize: config.style?.fontSize || 14, // Standardized to 14px (text-sm)
    fontWeight: config.style?.fontWeight || "normal",
    // Only set color/backgroundColor if explicitly provided AND not in dark mode, otherwise let Tailwind handle it
    // In dark mode, always use light text for visibility - don't apply inline color
    ...(config.style?.textColor && !config.disabled && !isDarkMode ? { color: config.style.textColor } : {}),
    ...(config.style?.backgroundColor && !config.disabled && !isDarkMode ? { backgroundColor: config.style.backgroundColor } : {}),
    fontFamily: "inherit", // Use system font stack
    textAlign: config.style?.textAlign || "left",
  };

  const dropdownStyles: React.CSSProperties = {
    borderRadius: `0 0 ${config.style?.borderRadius || 8}px ${config.style?.borderRadius || 8
      }px`,
    ...(dropdownPosition && {
      position: "absolute",
      top: `${dropdownPosition.top}px`,
      left: `${dropdownPosition.left}px`,
      width: `${dropdownPosition.width}px`,
    }),
  };

  return (
    <div className={`px-1 relative ${getWidthClass()}`} ref={dropdownRef} data-field-name={config.name}>
      {config.label && (
        <label className="block mb-0.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {config.label}
          {config.required && <span className="text-danger-500 dark:text-danger-400 ml-1">*</span>}
        </label>
      )}

      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <div
            ref={selectRef}
            tabIndex={config.disabled ? -1 : 0}
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-controls={isOpen ? `select-dropdown-${config.name}` : undefined}
            aria-activedescendant={isOpen && highlightedIndex >= 0 ? `option-${config.name}-${highlightedIndex}` : undefined}
            className={`w-full py-2 px-3 pr-9 text-sm font-normal text-left border border-1 outline-none transition-colors duration-200 select-none font-sans bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 ${config.disabled ? "cursor-not-allowed opacity-70 bg-gray-100 dark:bg-gray-700" : "cursor-pointer"
              } ${error
                ? "border-danger-500 dark:border-danger-500"
                : isOpen
                  ? "border-primary-500 dark:border-primary-400"
                  : "border-gray-300 dark:border-gray-600"
              } focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none`}
            style={selectStyles}
            onClick={() => {
              if (!config.disabled) {
                if (!isOpen) {
                  // Refresh lookup data for ALL independent lookups when dropdown is opened
                  const lookupName =
                    config.lookupName || (config as any).LookupName;
                  // Trigger onFocus callback if provided (for dependent lookups or additional logic)
                  // ComponentRenderer handles all lookup fetching, so we don't need to fetch here
                  if (onFocus) {
                    onFocus();
                  }
                }
                setIsOpen(!isOpen);
              }
            }}
            onKeyDown={(e) => {
              if (config.disabled) return;

              // Handle Enter key
              if (e.key === "Enter") {
                e.preventDefault();
                e.stopPropagation();
                if (isOpen && highlightedIndex >= 0 && highlightedIndex < config.options.length) {
                  // Select highlighted option
                  const option = config.options[highlightedIndex];
                  if (!option.disabled) {
                    handleOptionClick(option.value);
                  }
                } else if (!isOpen) {
                  if (onFocus) {
                    onFocus();
                  }
                  setIsOpen(true);
                  if (config.options.length > 0) {
                    setHighlightedIndex(0);
                  }
                }
                return;
              }

              // Handle Space to toggle dropdown
              if (e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                if (!isOpen) {
                  if (onFocus) {
                    onFocus();
                  }
                  setIsOpen(true);
                  if (config.options.length > 0) {
                    setHighlightedIndex(0);
                  }
                } else {
                  setIsOpen(false);
                  setHighlightedIndex(-1);
                }
                return;
              }

              // Handle Escape to close dropdown
              if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                setIsOpen(false);
                setHighlightedIndex(-1);
                return;
              }

              // Handle ArrowDown to open dropdown or navigate
              if (e.key === "ArrowDown") {
                e.preventDefault();
                e.stopPropagation();
                if (!isOpen) {
                  if (onFocus) {
                    onFocus();
                  }
                  setIsOpen(true);
                  if (config.options.length > 0) {
                    setHighlightedIndex(0);
                  }
                } else {
                  // Navigate to next option
                  setHighlightedIndex((prev) => {
                    const nextIndex = prev < config.options.length - 1 ? prev + 1 : 0;
                    // Scroll into view
                    setTimeout(() => {
                      const optionElement = optionRefs.current[nextIndex];
                      if (optionElement) {
                        optionElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
                      }
                    }, 0);
                    return nextIndex;
                  });
                }
                return;
              }

              // Handle ArrowUp to open dropdown or navigate
              if (e.key === "ArrowUp") {
                e.preventDefault();
                e.stopPropagation();
                if (!isOpen) {
                  if (onFocus) {
                    onFocus();
                  }
                  setIsOpen(true);
                  if (config.options.length > 0) {
                    setHighlightedIndex(config.options.length - 1);
                  }
                } else {
                  // Navigate to previous option
                  setHighlightedIndex((prev) => {
                    const nextIndex = prev > 0 ? prev - 1 : config.options.length - 1;
                    // Scroll into view
                    setTimeout(() => {
                      const optionElement = optionRefs.current[nextIndex];
                      if (optionElement) {
                        optionElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
                      }
                    }, 0);
                    return nextIndex;
                  });
                }
                return;
              }
            }}
          >
            <span className="text-gray-900 dark:text-gray-100">{getDisplayValue()}</span>
            <span
              className={`absolute right-3 top-1/2 -translate-y-1/2 transition-transform duration-200 pointer-events-none text-gray-400 dark:text-gray-500 ${isOpen ? "rotate-180" : "rotate-0"
                }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </div>
        </div>

        {config.PopupScreenId && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setIsQuickAddOpen(true);
            }}
            className="p-2 bg-primary-500 hover:bg-primary-600 text-white rounded-md transition-colors h-[38px] w-[38px] flex items-center justify-center shadow-sm"
            title="Add New"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        )}
      </div>

      {
        isOpen &&
        dropdownPosition &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={dropdownMenuRef}
            id={`select-dropdown-${config.name}`}
            role="listbox"
            className="dark:bg-gray-800 !w-fit text-sm border-2 border-gray-300 dark:border-gray-600 border-t-0 max-h-[200px] overflow-y-auto z-[9999] shadow-md bg-gray-50"
            style={dropdownStyles}
          >
            {config.options.map((option, index) => {
              const isWorkflowField = config.lookupName === "Approve" || config.name === "approve_status";
              const isRejectedOption = String(option.value).toLowerCase() === "rejected";
              const isSelectedValue = isSelected(option.value);
              const isHighlighted = highlightedIndex === index;

              return (
                <div
                  key={index}
                  ref={(el) => {
                    optionRefs.current[index] = el;
                  }}
                  id={`option-${config.name}-${index}`}
                  role="option"
                  aria-selected={isSelectedValue}
                  tabIndex={-1}
                  className={`py-2 px-3 text-sm font-normal text-left cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-0 transition-colors duration-200 font-sans text-gray-900 dark:text-gray-100 ${isHighlighted
                    ? isWorkflowField && isRejectedOption
                      ? "bg-red-200 dark:bg-red-800/50 text-red-800 dark:text-red-200 font-semibold border-l-4 border-l-red-500 dark:border-l-red-400"
                      : "bg-primary-200 dark:bg-primary-800/70 text-primary-900 dark:text-primary-100 font-medium"
                    : isSelectedValue
                      ? isWorkflowField && isRejectedOption
                        ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 font-semibold border-l-4 border-l-red-500 dark:border-l-red-400"
                        : "bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 font-medium"
                      : isWorkflowField && isRejectedOption
                        ? "hover:bg-red-50 dark:hover:bg-red-900/20"
                        : "hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevent input from losing focus
                    !option.disabled && handleOptionClick(option.value);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={isWorkflowField && isRejectedOption ? "text-red-600 dark:text-red-400 font-semibold" : ""}
                      style={{ opacity: option.disabled ? 0.5 : 1 }}
                    >
                      {option.label}
                    </span>
                    {config.multiple && isSelectedValue && (
                      <span className={isWorkflowField && isRejectedOption ? "text-red-600 dark:text-red-400 font-bold" : "text-success-600 dark:text-success-400 font-bold"}>
                        ✓
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>,
          document.body
        )
      }

      {
        (error || externalError) && (
          <div className="text-danger-500 dark:text-danger-400 text-xs mt-1">
            {error || externalError}
          </div>
        )
      }

      {
        config.helperText && !error && !externalError && (
          <div className="text-gray-500 dark:text-gray-400 text-xs mt-1">{config.helperText}</div>
        )
      }

      {
        config.multiple && config.maxSelections && Array.isArray(value) && (
          <div className="text-gray-500 dark:text-gray-400 text-xs mt-1 text-right">
            {value.length}/{config.maxSelections} selected
          </div>
        )
      }
      {
        config.PopupScreenId && (
          <QuickAddModal
            isOpen={isQuickAddOpen}
            popupScreenId={config.PopupScreenId}
            onClose={() => setIsQuickAddOpen(false)}
            onSuccess={handleQuickAddSuccess}
          />
        )
      }
    </div >
  );
};
